import * as React from 'react'
import {H3} from '@frontrow/console/src/components/typography'
import {Alert} from '@frontrow/console/src/components/ui/alert'
import {
  GenericSelector,
  createSelectorOptionAsItem,
  type SelectorItemT
} from '@frontrow/console/src/components/generic-selector'
import {UploadFileButton} from '@frontrow/console/src/components/upload-file-button'
import {exhaustive} from '@freckle/exhaustive'

import {type DistrictT} from '@frontrow/console/src/models/district'
import DistrictSearchButtonContainer from '@frontrow/console/src/components/common/district-search-button'
import {
  type CsvUploadLevelsT,
  type CsvUploadAssessmentT
} from '@frontrow/console/src/hooks/use-student-csv-upload'
import * as style from './csv-student-level-upload.module.scss'

type Props = {
  onPostFetch: (districts: Array<DistrictT>) => void
  district: DistrictT | undefined | null
  onFileUploaded: (file: File) => void
  uploadType: CsvUploadLevelsT
  uploadAssessmentType: CsvUploadAssessmentT
  overrideRecentPractice: boolean
  selectUploadType: (newType: CsvUploadLevelsT) => void
  selectUploadAssessmentType: (newType: CsvUploadAssessmentT) => void
  setOverrideRecentPractice: (newOverrideRecentPractice: boolean) => void
}

const UPLOAD_TYPES: Array<SelectorItemT<CsvUploadLevelsT>> = [
  createSelectorOptionAsItem('Math', 'Math'),
  createSelectorOptionAsItem('Ela', 'Ela')
]

const UPLOAD_ASSESSMENT_TYPES = [
  createSelectorOptionAsItem('MAP', 'MAP 2024'),
  createSelectorOptionAsItem('FastBridge', 'FastBridge'),
  createSelectorOptionAsItem('Ngmt', 'NGMT'),
  createSelectorOptionAsItem('Old MAP', 'Old NWEA/MAP')
]

export default function CsvStudentLevelUpload(props: Props): React.ReactElement {
  const {
    onPostFetch,
    district,
    onFileUploaded,
    uploadType,
    selectUploadType,
    uploadAssessmentType,
    selectUploadAssessmentType,
    setOverrideRecentPractice,
    overrideRecentPractice
  } = props

  const districtInfo =
    district === null ? (
      <H3 addClass={style.districtInfo}>District not found</H3>
    ) : district === undefined ? null : (
      <H3 addClass={style.districtInfo}>{district.name}</H3>
    )

  return (
    <div className={style.wrapper}>
      <Alert type="info">
        To ensure students don't lose progress, we will not update a student's level if they have
        practiced within the past 90 days. Select the "Override recent student practice" option if
        you wish to bypass this check.
      </Alert>
      <div className={style.setUploadTypeHeader}>
        <H3>Set Student Levels in:</H3>
        <GenericSelector
          id="csvUploadTypeSelector"
          className={style.uploadTypeSelector}
          selection={uploadType}
          collection={UPLOAD_TYPES}
          onChangeSelection={selectUploadType}
        />
      </div>
      <div className={style.setUploadTypeHeader}>
        <H3>Assessment type:</H3>
        <GenericSelector
          id="csvUploadAssessmentTypeSelector"
          className={style.uploadAssessmenmtTypeSelector}
          selection={uploadAssessmentType.tag}
          collection={UPLOAD_ASSESSMENT_TYPES}
          onChangeSelection={tag => {
            switch (tag) {
              case 'Old MAP':
                selectUploadAssessmentType({tag: 'Old MAP'})
                return
              case 'MAP':
                selectUploadAssessmentType({tag: 'MAP'})
                return
              case 'FastBridge':
                selectUploadAssessmentType({tag, scoreColumnName: null})
                return
              case 'Ngmt':
                selectUploadAssessmentType({tag: 'Ngmt'})
                return
              default:
                exhaustive(tag as never)
            }
          }}
        />
      </div>
      {uploadType && (
        <div>
          <label htmlFor="overrideRecentPractice">
            <H3>Override recent student practice:</H3>
          </label>{' '}
          <input
            type="checkbox"
            id="overrideRecentPractice"
            name="overrideRecentPractice"
            checked={overrideRecentPractice}
            onChange={() => setOverrideRecentPractice(!overrideRecentPractice)}
          />
        </div>
      )}
      {uploadAssessmentType.tag === 'FastBridge' && (
        <div className={style.setUploadTypeHeader}>
          <H3>Score column name:</H3>
          <input
            type="text"
            className={style.scoreColumnInput}
            placeholder="Score Column"
            value={uploadAssessmentType.scoreColumnName!}
            onChange={e => {
              selectUploadAssessmentType({
                tag: 'FastBridge',
                scoreColumnName: e.target.value
              })
            }}
          />
        </div>
      )}
      <div className={style.buttonDistrictInfo}>
        <DistrictSearchButtonContainer onPostFetch={onPostFetch} />
        {districtInfo}
      </div>

      <UploadFileButton
        buttonText={`Upload CSV of Student Levels in ${uploadType}`}
        onPostFileUpload={onFileUploaded}
        isDisabled={disableUpload(district, uploadAssessmentType, uploadType)}
      />
    </div>
  )
}

function disableUpload(
  district: DistrictT | undefined | null,
  uploadAssessmentType: CsvUploadAssessmentT,
  type: CsvUploadLevelsT
): boolean {
  if (district === null || district === undefined) {
    return true
  }
  switch (uploadAssessmentType.tag) {
    case 'Old MAP':
    case 'MAP':
      return false
    case 'FastBridge':
      return (
        uploadAssessmentType.scoreColumnName === null || uploadAssessmentType.scoreColumnName === ''
      )
    case 'Ngmt':
      return type !== 'Math'
    default:
      return exhaustive(uploadAssessmentType)
  }
}
